import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Sparkles, Heart, Star, CheckCircle } from 'lucide-react';

interface WelcomePopupProps {
  isVisible: boolean;
  onClose: () => void;
  isNewUser?: boolean;
}

export const WelcomePopup: React.FC<WelcomePopupProps> = ({ isVisible, onClose, isNewUser = false }) => {
  const [showContent, setShowContent] = useState(false);

  useEffect(() => {
    if (isVisible) {
      // تأخير بسيط لإظهار المحتوى بعد ظهور البوب أب
      const timer = setTimeout(() => setShowContent(true), 300);
      return () => clearTimeout(timer);
    }
  }, [isVisible]);

  const handleClose = () => {
    setShowContent(false);
    setTimeout(onClose, 200);
  };

  const getWelcomeMessage = () => {
    if (isNewUser) {
      return {
        title: 'أهلاً وسهلاً بك في Tawazun! 🎉',
        subtitle: 'منصة تطوير الذات والتحفيز',
        message: 'نحن متحمسون لرحلة تطويرك معنا. ابدأ رحلتك نحو الأفضل!',
        features: ['تتبع العادات والأهداف', 'مجتمع داعم ومحفز', 'إنجازات وتحديات ممتعة'],
      };
    } else {
      return {
        title: 'مرحباً بعودتك! 🌟',
        subtitle: 'نحن سعداء برؤيتك مرة أخرى',
        message: 'استمر في رحلتك نحو التطوير والتحسين. أنت على الطريق الصحيح!',
        features: ['تابع تقدمك اليومي', 'شارك إنجازاتك مع المجتمع', 'اكتشف تحديات جديدة'],
      };
    }
  };

  const welcomeData = getWelcomeMessage();

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
          onClick={handleClose}
        >
          <motion.div
            initial={{ scale: 0.8, opacity: 0, y: 50 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.8, opacity: 0, y: 50 }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
            className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header with gradient background */}
            <div className="relative bg-gradient-to-br from-blue-500 via-purple-500 to-pink-500 p-6 text-white">
              <motion.div initial={{ rotate: 0 }} animate={{ rotate: 360 }} transition={{ duration: 2, repeat: Infinity, ease: 'linear' }} className="absolute top-4 right-4">
                <Sparkles size={24} />
              </motion.div>

              <button onClick={handleClose} className="absolute top-4 left-4 p-2 rounded-full bg-white/20 hover:bg-white/30 transition-colors">
                <X size={20} className="text-white" />
              </button>

              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }} className="text-center">
                <motion.h2 initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.5 }} className="text-2xl font-bold mb-2">
                  {welcomeData.title}
                </motion.h2>
                <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.7 }} className="text-blue-100 text-sm">
                  {welcomeData.subtitle}
                </motion.p>
              </motion.div>
            </div>

            {/* Content */}
            <div className="p-6">
              <motion.p initial={{ opacity: 0 }} animate={{ opacity: showContent ? 1 : 0 }} transition={{ delay: 0.9 }} className="text-gray-700 text-center mb-6 leading-relaxed">
                {welcomeData.message}
              </motion.p>

              {/* Features list */}
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: showContent ? 1 : 0, y: showContent ? 0 : 20 }} transition={{ delay: 1.1 }} className="space-y-3">
                {welcomeData.features.map((feature, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: showContent ? 1 : 0, x: showContent ? 0 : -20 }}
                    transition={{ delay: 1.3 + index * 0.1 }}
                    className="flex items-center space-x-3 rtl:space-x-reverse"
                  >
                    <motion.div initial={{ scale: 0 }} animate={{ scale: showContent ? 1 : 0 }} transition={{ delay: 1.5 + index * 0.1, type: 'spring' }} className="flex-shrink-0">
                      {index === 0 && <Heart className="w-5 h-5 text-red-500" />}
                      {index === 1 && <Star className="w-5 h-5 text-yellow-500" />}
                      {index === 2 && <CheckCircle className="w-5 h-5 text-green-500" />}
                    </motion.div>
                    <span className="text-gray-600 text-sm">{feature}</span>
                  </motion.div>
                ))}
              </motion.div>

              {/* Action button */}
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: showContent ? 1 : 0, y: showContent ? 0 : 20 }} transition={{ delay: 1.6 }} className="mt-6">
                <button
                  onClick={handleClose}
                  className="w-full bg-gradient-to-r from-blue-500 to-purple-500 text-white py-3 px-6 rounded-xl font-semibold hover:from-blue-600 hover:to-purple-600 transition-all duration-300 transform hover:scale-105 shadow-lg"
                >
                  {isNewUser ? 'ابدأ رحلتي' : 'استمر في التطوير'}
                </button>
              </motion.div>
            </div>

            {/* Decorative elements */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 0.1 }}
              transition={{ delay: 1 }}
              className="absolute -top-10 -right-10 w-20 h-20 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-full blur-xl"
            />
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 0.1 }}
              transition={{ delay: 1.2 }}
              className="absolute -bottom-10 -left-10 w-16 h-16 bg-gradient-to-br from-pink-400 to-purple-500 rounded-full blur-xl"
            />
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
